import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/EmptyState";
import { ExportXlsxButton } from "@/components/ExportXlsxButton";
import { Trophy, Calendar, MapPin, Filter, Search, X, ExternalLink, Building2 } from "lucide-react";
import { showError } from "@/lib/errors";

const LEVELS = [
  { v: "local", label: "Local" },
  { v: "territorial", label: "Territorial" },
  { v: "national", label: "National" },
  { v: "regional", label: "Régional" },
  { v: "international", label: "International" },
  { v: "professionnel", label: "Professionnel" },
];

const STATUSES = [
  { v: "prevue", label: "Prévue" },
  { v: "en_cours", label: "En cours" },
  { v: "terminee", label: "Terminée" },
  { v: "annulee_reportee", label: "Annulée / reportée" },
];

const SOURCES = [
  { v: "admin", label: "Administration AITO.PF" },
  { v: "copf", label: "COPF" },
  { v: "federation", label: "Fédération" },
  { v: "club", label: "Club" },
  { v: "athlete", label: "Athlète" },
  { v: "contributor", label: "Contributeur" },
  { v: "calendar", label: "Calendrier interne" },
];

const ALL = "__all__";

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function CompetitionCard({ c }) {
  return (
    <Link to={`/competitions/${c.slug || c.id}`} data-testid="competition-card" className="block group">
      <Card className="p-4 h-full aito-card-hover flex flex-col gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          {c.discipline && <Badge variant="outline" className="text-[10px] capitalize">{c.discipline}</Badge>}
          {c.level && <Badge variant="secondary" className="text-[10px]">{c.level}</Badge>}
          {c.competition_status && c.competition_status !== "prevue" && (
            <Badge variant="outline" className="text-[10px] capitalize">{c.competition_status.replace("_", " ")}</Badge>
          )}
        </div>
        <h3 className="font-display font-semibold text-base leading-tight line-clamp-2 group-hover:text-[hsl(var(--aito-lagoon))]">{c.name}</h3>
        <div className="space-y-1 text-xs text-muted-foreground mt-auto">
          {c.date_start && <div className="flex items-center gap-1.5"><Calendar className="h-3 w-3 shrink-0" />{fmtDate(c.date_start)}{c.date_end && c.date_end !== c.date_start ? ` → ${fmtDate(c.date_end)}` : ""}</div>}
          {(c.location || c.island) && (
            <div className="flex items-center gap-1.5"><MapPin className="h-3 w-3 shrink-0" />{[c.location, c.island].filter(Boolean).join(" · ")}</div>
          )}
          {c.organizer && <div className="flex items-center gap-1.5"><Building2 className="h-3 w-3 shrink-0" />{c.organizer}</div>}
        </div>
        <div className="flex gap-1.5 flex-wrap pt-1 border-t mt-1">
          {c.source_type && <Badge variant="outline" className="text-[10px]">{c.source_type}</Badge>}
          {c.official_url && (
            <a href={c.official_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[10px] text-[hsl(var(--aito-lagoon))] inline-flex items-center gap-0.5 hover:underline">
              Officiel <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
          {c.results_url && (
            <a href={c.results_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[10px] text-[hsl(var(--aito-lagoon))] inline-flex items-center gap-0.5 hover:underline">
              Résultats <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
        </div>
      </Card>
    </Link>
  );
}

export default function CompetitionsPage() {
  const [items, setItems] = useState(null);
  const [disciplines, setDisciplines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [year, setYear] = useState(ALL);
  const [discipline, setDiscipline] = useState(ALL);
  const [level, setLevel] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [sourceType, setSourceType] = useState(ALL);

  useEffect(() => { api.get("/disciplines").then((r) => setDisciplines(r.data.items || [])).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 200 };
    if (year !== ALL) params.year = parseInt(year);
    if (discipline !== ALL) params.discipline = discipline;
    if (level !== ALL) params.level = level;
    if (status !== ALL) params.status = status;
    if (sourceType !== ALL) params.source_type = sourceType;
    api.get("/competitions", { params })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => { setItems([]); showError(e, { prefix: "Compétitions :" }); })
      .finally(() => setLoading(false));
  }, [year, discipline, level, status, sourceType]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((c) =>
      (c.name || "").toLowerCase().includes(q)
      || (c.location || "").toLowerCase().includes(q)
      || (c.island || "").toLowerCase().includes(q)
      || (c.organizer || "").toLowerCase().includes(q),
    );
  }, [items, search]);

  const years = useMemo(() => {
    const set = new Set();
    (items || []).forEach((c) => { if (c.date_start) set.add(new Date(c.date_start).getUTCFullYear()); });
    const cur = new Date().getUTCFullYear();
    [cur - 1, cur, cur + 1, cur + 2].forEach((y) => set.add(y));
    return Array.from(set).sort((a, b) => b - a);
  }, [items]);

  const hasFilters = year !== ALL || discipline !== ALL || level !== ALL || status !== ALL || sourceType !== ALL || search;
  const clearAll = () => { setYear(ALL); setDiscipline(ALL); setLevel(ALL); setStatus(ALL); setSourceType(ALL); setSearch(""); };

  return (
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-8" data-testid="competitions-page">
      <header className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold flex items-center gap-2">
            <Trophy className="h-7 w-7 text-[hsl(var(--aito-gold))]" />
            Compétitions
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Toutes les compétitions sportives polynésiennes déclarées sur AITO.PF — calendrier officiel, sources fédérations, clubs, athlètes et contributeurs.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ExportXlsxButton
            endpoint="/exports-xlsx/competitions"
            params={{
              q: search,
              year: year !== ALL ? year : undefined,
              discipline: discipline !== ALL ? discipline : undefined,
              level: level !== ALL ? level : undefined,
              status: status !== ALL ? status : undefined,
              source_type: sourceType !== ALL ? sourceType : undefined,
            }}
            filename="AITO_competitions.xlsx"
            testid="competitions-export-xlsx"
          />
          <div className="text-xs text-muted-foreground">{filtered.length} résultat{filtered.length > 1 ? "s" : ""}</div>
        </div>
      </header>

      {/* Filtres */}
      <Card className="p-4 mb-6 space-y-3" data-testid="competitions-filters">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
          <span className="text-sm font-medium">Filtres</span>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearAll} data-testid="comp-clear-filters" className="ml-auto text-xs h-7">
              <X className="h-3 w-3 mr-1" />Réinitialiser
            </Button>
          )}
        </div>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="comp-search"
            placeholder="Rechercher par nom, lieu, organisateur…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger data-testid="comp-filter-year"><SelectValue placeholder="Année" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toutes les années</SelectItem>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={discipline} onValueChange={setDiscipline}>
            <SelectTrigger data-testid="comp-filter-discipline"><SelectValue placeholder="Discipline" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value={ALL}>Toutes les disciplines</SelectItem>
              {disciplines.map((d) => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger data-testid="comp-filter-level"><SelectValue placeholder="Niveau" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous niveaux</SelectItem>
              {LEVELS.map((l) => <SelectItem key={l.v} value={l.v}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="comp-filter-status"><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous statuts</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sourceType} onValueChange={setSourceType}>
            <SelectTrigger data-testid="comp-filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toutes sources</SelectItem>
              {SOURCES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Liste */}
      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="competitions-loading">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-44 w-full" />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Trophy}
          title={hasFilters ? "Aucun résultat" : "Aucune compétition référencée."}
          description={hasFilters
            ? "Modifie ou réinitialise tes filtres pour élargir la recherche."
            : "Les compétitions, leurs résultats et podiums seront archivés ici dès leur déclaration."
          }
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="competitions-grid">
          {filtered.map((c) => <CompetitionCard key={c.id} c={c} />)}
        </div>
      )}
    </div>
  );
}
